/**
 * 3D COCKPIT's scene half: the dashboard quad, and the snapshot the panel hands it.
 *
 * `cockpit-dash-quad.ts` owns the arithmetic and imports no `three`; this is the
 * object that carries it. The split is `panel-layout.ts`/`panel-render.ts`'s and
 * `cockpit-projection.ts`'s: the placement is a pure function with a test, and
 * the mesh around it is a dozen lines with none.
 *
 * ## The composition is shared; the canvas is not
 *
 * The composed 320x200 cockpit frame already exists — `CockpitPanel.tsx` builds
 * one per view and repaints the live instruments into it at the HUD's own rate,
 * which is where `REALISTIC INSTRUMENTS`, the muzzle flash and the accumulated
 * bullet holes all come from. Rebuilding any of that here would be a second copy
 * of a picture that is hard to keep identical and easy to let drift, so the panel
 * keeps composing and this draws the result.
 *
 * What crosses the boundary is a **snapshot**, not the panel's surface: an
 * `ImageBitmap` taken when the panel repaints (`snapshotDashFrame`). After the
 * copy neither side owns anything the other is touching, which is what makes the
 * `DashCanvasFeed` handoff safe: it is a plain mutable object handed to both by
 * whichever route owns them, exactly the shape `flight-scene.ts` already uses
 * for the ground and scenery swap refs (`{ current: GroundQuery }`).
 *
 * It was **also tried as a cure** for the open defect below and is not one: with
 * the setting on, the WebGPU canvas stops presenting new frames and every way of
 * reading it back stalls, and it does so identically whether the quad textures
 * off a live canvas or off a detached `ImageBitmap`. The snapshot stays because
 * it is the right ownership either way; the defect is still open
 * (`docs/engineering/lessons.md`).
 *
 * `version` is what makes it a feed rather than a poll. A texture has to be told
 * when its source changed, and the panel knows: it bumps the counter when it
 * publishes. Uploading unconditionally every frame would work and would cost a
 * 320x200 upload at display rate for a picture that changes twelve times a
 * second.
 */

import * as THREE from "three/webgpu";
import {
  dashFrameIsPainted,
  dashQuadCorners,
  dashUvWindow,
  DASH_DEPTH_M,
  type DashCanvasFeed,
  type DashFrameSource,
  type DashViewport,
  type LogicalRect,
} from "./cockpit-dash-quad.ts";
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from "./panel-layout.ts";
import type { EyeOffset } from "./cockpit-view.ts";

/** The pilot's eye in the aircraft's body frame — the same vector `gun-muzzles.ts` builds, and the quad's own origin. */
function eyeBodyOffset(eye: EyeOffset): THREE.Vector3 {
  return new THREE.Vector3(eye.rightM, eye.upM, -eye.forwardM);
}

/**
 * What an in-page probe can ask the quad, chosen so that **no reading in it can
 * be true while the dashboard is invisible**.
 *
 * Phase 1 shipped with a probe that reported visible, mapped, uploaded and
 * positioned-to-six-decimals, and the dashboard drew nothing for weeks: the mesh
 * had been detached from the scene by `loadAircraft` (`scene-boot.ts`'s
 * `installAircraftMesh`) and every one of those readings was a property of an
 * orphan. `inScene` is that miss, written down; `painted` is the other half —
 * a quad can be attached, visible and textured from a cleared canvas.
 *
 * `describeCockpit3d` renders this into the one string the container carries as
 * `data-dash-quad`, so a CDP probe needs no hook into the scene at all — the
 * same trick `CockpitPanel.tsx`'s `data-cockpit-frame` already plays.
 */
export interface Cockpit3dQuadReport {
  /** Whether the mesh is actually attached to something — the reading phase 1 did not have. */
  readonly inScene: boolean;
  readonly visible: boolean;
  /** How many published snapshots have been uploaded to the texture. */
  readonly uploads: number;
  /** `ImageBitmap`, `HTMLCanvasElement`, or `none` before the first publish. */
  readonly source: string;
  /** Whether the last measured snapshot had paint in it and more than one colour (`dashFrameStats`). */
  readonly painted: boolean | undefined;
  /** The last snapshot's mean luminance and variance, rounded — `undefined` until one has been measured. */
  readonly mean: number | undefined;
  readonly variance: number | undefined;
}

export interface Cockpit3dQuad {
  /** Parent this to the aircraft's pose group; it is in the body frame. */
  readonly object: THREE.Object3D;
  /** The viewport the registration is against — re-set on every resize, like the painted breeches. */
  setViewport(viewport: DashViewport): void;
  setVisible(visible: boolean): void;
  /** Reads one repaint out of the feed; a no-op when nothing has changed. */
  update(feed: DashCanvasFeed): void;
  /** The probe's whole surface — see `Cockpit3dQuadReport`. */
  report(feed: DashCanvasFeed): Cockpit3dQuadReport;
  dispose(): void;
}

/** One line of `key=value`, stable enough to compare between frames and short enough for a DOM attribute. */
export function describeCockpit3d(report: Cockpit3dQuadReport): string {
  const round = (value: number | undefined): string => (value === undefined ? "?" : value.toFixed(1));
  return [
    `inScene=${report.inScene ? 1 : 0}`,
    `visible=${report.visible ? 1 : 0}`,
    `uploads=${report.uploads}`,
    `source=${report.source}`,
    `painted=${report.painted === undefined ? "?" : report.painted ? 1 : 0}`,
    `mean=${round(report.mean)}`,
    `var=${round(report.variance)}`,
  ].join(" ");
}

/**
 * An empty 320x200 frame, so the quad's material has a `map` — and therefore its
 * final pipeline — from construction. Transparent, so a quad shown before the
 * panel's first snapshot draws nothing rather than a black rectangle.
 */
function blankFrame(): HTMLCanvasElement | undefined {
  // `undefined` where there is no DOM at all — a headless test constructs the
  // quad to check its material and never renders it, and `THREE.Texture` takes
  // the absence perfectly well.
  if (typeof document === "undefined") return undefined;
  const canvas = document.createElement("canvas");
  canvas.width = LOGICAL_WIDTH;
  canvas.height = LOGICAL_HEIGHT;
  return canvas;
}

/** Four corners and two triangles, positions rebuilt in place whenever the registration moves. */
function writeCorners(geometry: THREE.BufferGeometry, eye: EyeOffset, art: LogicalRect, viewport: DashViewport): void {
  const corners = dashQuadCorners(art, viewport, DASH_DEPTH_M);
  const origin = eyeBodyOffset(eye);
  const positions = geometry.getAttribute("position") as THREE.BufferAttribute;
  const order = [corners.topLeft, corners.topRight, corners.bottomLeft, corners.bottomRight];
  for (const [index, corner] of order.entries()) {
    positions.setXYZ(index, origin.x + corner.x, origin.y + corner.y, origin.z + corner.z);
  }
  positions.needsUpdate = true;
  geometry.computeBoundingSphere();
}

/**
 * The dashboard quad for one aircraft's eye point.
 *
 * `DoubleSide` rather than a computed winding: the quad is only ever shown in
 * cockpit mode, where exactly one side of it can be seen, and a face that
 * vanishes because two vertices were listed the other way round is a bug whose
 * only symptom is an invisible dashboard — the kind the G1 lesson says a
 * screenshot will not catch.
 *
 * `depthWrite: false` because the canvas is mostly transparent: the dash band is
 * opaque but everything above the gun breeches is cleared, and a transparent
 * plane half a metre in front of the pilot that wrote depth would erase the sky
 * behind it.
 *
 * **`depthTest: false` is a deliberate deviation from the design note**, which
 * had the quad depth-tested so the aeroplane's own coaming would occlude it.
 * Two reasons, and neither is a measurement — the visual check this wanted could
 * not be made (see the capture lesson in `docs/engineering/lessons.md`), so it is
 * chosen for what fails safely:
 *
 * - **A dashboard is the nearest thing in a cockpit.** Nothing on the aeroplane
 *   is ever in front of it, so a depth test can only ever be a way to *lose* it.
 *   The painted panel has always drawn over everything for exactly this reason;
 *   this is the same statement made in the scene, where it can also turn with the
 *   aeroplane.
 * - **The failure modes are not symmetric.** A depth test that comes out wrong —
 *   because the enhanced Camel's cockpit opening is cut from the *decking*
 *   (`airframe.py`'s `cockpit_ranges`) and the decking ahead of it may well lie
 *   between the pilot's eye and a quad half a metre in front of him — is an
 *   *invisible dashboard*. A missing occlusion is a coaming that does not overlap
 *   the panel's top edge, which is a phase-2 taste question the design note
 *   already schedules.
 *
 * `renderOrder` keeps it after the airframe so the transparent pass draws it over
 * what has already been laid down — see `DASH_QUAD_RENDER_ORDER` for why "after
 * the airframe" was not far enough.
 */
/**
 * The quad's geometry: four corners the registration will fill in, a unit-square
 * UV the texture's own offset/repeat then crops, and two triangles.
 */
function quadGeometry(): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(new Float32Array([0, 1, 1, 1, 0, 0, 1, 0]), 2));
  geometry.setIndex([0, 2, 1, 2, 3, 1]);
  return geometry;
}

/**
 * The dashboard's texture, blank but **present**, and its material — built
 * together because the point of the pair is that they are never apart.
 *
 * One texture for the life of the quad, its *source* swapped per snapshot:
 * building a new `THREE.Texture` per repaint would hand the backend a new object
 * twelve times a second and rebuild the material's binding with it.
 *
 * It starts with a blank frame rather than no image at all, so the material has
 * its `map` from the first instant. A material that gains a map later needs a
 * **different pipeline**, and the one thing this app knows about WebGPU
 * pipelines is that compiling one mid-render is not free
 * (`three/precompile-scene.ts` exists for it). `precompileScene` does reach this
 * quad — it forces `visible` over the whole scene — but it can only compile the
 * material as it finds it, so finding it without a map meant the pipeline
 * actually used was never precompiled at all. The blank is the same size as a
 * real frame, so the first snapshot is a plain upload and not a texture rebuild.
 */
function dashSurface(): { texture: THREE.Texture; material: THREE.MeshBasicMaterial } {
  const texture = new THREE.Texture(blankFrame());
  // The same treatment every other piece of the original's own art gets:
  // nearest-neighbour both ways so a dial stays the pixels it was drawn as, and
  // no colour transform (`three/webgpu-support.ts`).
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false, depthTest: false, side: THREE.DoubleSide });
  return { texture, material };
}

/**
 * **Last of everything.** Above the propeller disc's `1`, the searchlight beams'
 * `3` and the tracers' `4` (`three/tracer-pool.ts`'s own note is the other half
 * of this ladder).
 *
 * Phase 1 gave the quad `1`, which is the propeller disc's own number, and the
 * user reported the consequence the moment 3D COCKPIT and ENHANCED MODELS were
 * on together: *"the propeller draws on top of the sprites"*. A tie in
 * `renderOrder` leaves the order to three's own transparent sort, which keys on
 * each object's **world origin** projected into view space. The quad hangs on
 * the player's pose group at the pilot's eye, so its origin is essentially *at*
 * the camera; `PropellerDiscs`' two `InstancedMesh`es hang on a group at the
 * world origin and carry absolute per-instance matrices, so theirs is wherever
 * the map's datum happens to be — a kilometre or three away at any normal
 * altitude. Sorted back to front that puts the disc **first** and the dashboard
 * **second** only by accident of where the aeroplane is flying, and the disc's
 * own `depthTest` then lets it draw over a quad that wrote no depth.
 *
 * Making the number explicit and larger settles it without touching the disc:
 * the dashboard is drawn after every transparent thing in the cockpit, and
 * `depthTest: false` means nothing can be in front of it. Which is the same
 * sentence the depth flags are already chosen for, said once more in the sort.
 *
 * A tracer that crosses the dash band is covered by it too, and that is correct:
 * a round in flight is on the far side of an opaque instrument panel. Above the
 * band the quad is transparent and the tracer shows through.
 */
export const DASH_QUAD_RENDER_ORDER = 5;

export function createCockpit3dQuad(eye: EyeOffset): Cockpit3dQuad {
  const geometry = quadGeometry();
  const { texture, material } = dashSurface();
  const mesh = new THREE.Mesh(geometry, material);
  mesh.visible = false;
  // Last of everything, so "nothing is in front of the dashboard" is true of the
  // draw order as well as of the depth test.
  mesh.renderOrder = DASH_QUAD_RENDER_ORDER;
  mesh.frustumCulled = false;

  let viewport: DashViewport | undefined;
  let art: LogicalRect | undefined;
  let uploaded = -1;
  let uploads = 0;
  /** The snapshot the GPU is reading. Closed only once a *newer* one has been uploaded in its place. */
  let shown: DashFrameSource | undefined;

  function reregister(): void {
    if (!viewport || !art) return;
    writeCorners(geometry, eye, art, viewport);
    const window = dashUvWindow(art);
    texture.offset.set(window.offsetX, window.offsetY);
    texture.repeat.set(window.repeatX, window.repeatY);
  }

  /** An `ImageBitmap` holds a decoded surface until it is closed; a canvas is the panel's and is not ours to touch. */
  function release(source: DashFrameSource | undefined): void {
    if (source && "close" in source) source.close();
  }

  return {
    object: mesh,
    setViewport(next) {
      viewport = next;
      reregister();
    },
    setVisible(visible) {
      mesh.visible = visible && shown !== undefined;
    },
    update(feed) {
      if (!feed.source || !feed.art) return;
      const moved = !art || art.left !== feed.art.left || art.top !== feed.art.top || art.right !== feed.art.right || art.bottom !== feed.art.bottom;
      art = feed.art;
      if (moved) reregister();
      if (uploaded === feed.version) return;
      const previous = shown;
      texture.image = feed.source as HTMLCanvasElement;
      texture.needsUpdate = true;
      shown = feed.source;
      uploaded = feed.version;
      uploads++;
      // After the swap, never before: the frame being replaced was the one the
      // last submitted draw read from.
      if (previous !== shown) release(previous);
    },
    report(feed) {
      return {
        // `parent`, not `visible`: an object taken out of the graph keeps every
        // flag it had, which is exactly how this quad went missing.
        inScene: mesh.parent !== null,
        visible: mesh.visible,
        uploads,
        // The constructor's own name rather than an `instanceof`, so this reads
        // the same in a headless test as in a browser (`blankFrame`'s note).
        source: shown ? shown.constructor.name : "none",
        painted: feed.stats ? dashFrameIsPainted(feed.stats) : undefined,
        mean: feed.stats?.mean,
        variance: feed.stats?.variance,
      };
    },
    dispose() {
      release(shown);
      texture.dispose();
      material.dispose();
      geometry.dispose();
    },
  };
}
